import React, { useEffect, useState } from "react";
import { Modal, Button, Checkbox } from "antd";
import {
  RotateLeftOutlined,
  RotateRightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from "@ant-design/icons";
import { PDFDocument } from "pdf-lib";
 
interface SelectedFilePreviewModalProps {
  isVisible: boolean;
  onClose: () => void;
  fileUrl: string;
  fileName: string;
  handleUpload: any;
  showUploadButton: boolean;
  documentType?: string;
  key?: any;
  isLoading?: boolean;
  handleUploadFile?: any;
  isHospitalBill?: boolean;
}
 
const SelectedFilePreviewModal: React.FC<SelectedFilePreviewModalProps> = ({
  isVisible,
  onClose,
  fileUrl,
  fileName,
  handleUpload,
  showUploadButton,
  documentType,
  isLoading,
  handleUploadFile,
  isHospitalBill
}) => {
  const isPdf = fileName.toLowerCase().endsWith(".pdf");
 
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotationAngle, setRotationAngle] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  const [selectAll, setSelectAll] = useState(false);
 
  useEffect(() => {
    setZoomLevel(1);
    setRotationAngle(0);
    setSelectedPages([]);
    setSelectAll(false);
 
    if (isPdf) {
      loadPdfPages();
    } else {
      setNumPages(1);
      setSelectedPages([1]);
    }
  }, [fileUrl]);
 
  const loadPdfPages = async () => {
    try {
      const response = await fetch(fileUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      const totalPages = pdfDoc.getPageCount();
      setNumPages(totalPages);
      setSelectedPages(Array.from({ length: totalPages }, (_, i) => i + 1));
      setSelectAll(true);
    } catch (error) {
      console.error("Error loading PDF:", error);
    }
  };
 
  const handlePageSelection = (pageNum: number) => {
    setSelectedPages((prev) =>
      prev.includes(pageNum)
        ? prev.filter((p) => p !== pageNum)
        : [...prev, pageNum].sort((a, b) => a - b)
    );
  };
 
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedPages(Array.from({ length: numPages }, (_, i) => i + 1));
    } else {
      setSelectedPages([]);
    }
  };
 
  const extractSelectedPages = async () => {
    if (!isPdf) {
      handleUpload();
      return;
    }
 
    try {
      const response = await fetch(fileUrl);
      const arrayBuffer = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
 
      const newPdfDoc = await PDFDocument.create();
 
      // Copy selected pages (convert to 0-based index)
      const pageIndices = selectedPages.map((p) => p - 1);
      const copiedPages = await newPdfDoc.copyPages(pdfDoc, pageIndices);
 
      copiedPages.forEach((page: any) => {
        newPdfDoc.addPage(page);
      });
 
      // Generate PDF bytes
      const pdfBytes: any = await newPdfDoc.save();
 
      // Create a new File/Blob to send to backend
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const file = new File([blob], fileName, { type: "application/pdf" });
 
      // Pass the modified file to handleUpload
      handleUpload(file);
    } catch (error) {
      console.error("Error extracting pages:", error);
    }
  };
 
  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => Math.min(prevZoom + 0.1, 3));
  };
 
  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => Math.max(prevZoom - 0.1, 0.5));
  };
 
  const handleRotateLeft = () => setRotationAngle((prev) => prev - 90);
  const handleRotateRight = () => setRotationAngle((prev) => prev + 90);
 
  return (
    <div className="preview-modal">
      <Modal
        title={`Preview: ${fileName}`}
        open={isVisible}
        onCancel={onClose}
        footer={[
          showUploadButton && (
            <Button
              key="upload"
              type="primary"
              onClick={extractSelectedPages}
              loading={isLoading}
              disabled={selectedPages.length === 0}
              style={{ marginTop: "0.6rem" }}
            >
              Upload Selected Pages ({selectedPages.length})
            </Button>
          ),
          <Button key="close" onClick={onClose} style={{ marginTop: "0.8rem" }}>
            Close
          </Button>,
        ]?.filter(Boolean)}
        width={1000}
        bodyStyle={{ height: "120vh" }}
        wrapClassName="preview-modal"
      >
        {/* Page Selection Controls */}
        {numPages > 1 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "10px",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
            }}
          >
            <div style={{ marginBottom: "10px" }}>
              {isHospitalBill ? (<p style={{ fontWeight: "bold", color: "#ff4d4f" }}>
                  Please deselect bill summary page from below
                </p>) : (<Checkbox
                checked={selectAll}
                onChange={(e) => handleSelectAll(e.target.checked)}
                style={{ fontWeight: "bold" }}
              >
                Select All Pages ({numPages})
              </Checkbox>)}
              
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {Array.from({ length: numPages }, (_, i) => i + 1).map(
                (pageNum) => (
                  <Checkbox
                    key={pageNum}
                    checked={selectedPages.includes(pageNum)}
                    onChange={() => handlePageSelection(pageNum)}
                  >
                    Page {pageNum}
                  </Checkbox>
                )
              )}
            </div>
          </div>
        )}
 
        {isPdf ? (
          <object
            data={fileUrl}
            type="application/pdf"
            width="100%"
            height="90%"
          >
            <embed
              src={fileUrl}
              type="application/pdf"
              width="100%"
              height="100%"
            />
            <p>
              This browser does not support PDFs. Please download the PDF to
              view it:
              <a href={fileUrl} download={fileName}>
                Download PDF
              </a>
              .
            </p>
          </object>
        ) : (
          <>
            <div
              style={{
                overflow: "auto",
                width: "100%",
                height: "120vh",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                marginTop: 20,
              }}
            >
              <div
                style={{
                  minWidth: `${100 * zoomLevel}%`,
                  minHeight: `${100 * zoomLevel}%`,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <img
                  src={fileUrl}
                  alt={fileName}
                  style={{
                    transform: `scale(${zoomLevel}) rotate(${rotationAngle}deg)`,
                    transition: "transform 0.3s ease",
                    maxWidth: "none",
                    maxHeight: "none",
                    width: "100%",
                    height: "auto",
                  }}
                />
              </div>
            </div>
            <div className="zoom-controls" style={{ marginTop: "0.3rem" }}>
              <Button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 0.5}
                style={{ marginRight: "0.5rem" }}
              >
                <ZoomOutOutlined />
              </Button>
              <Button onClick={handleZoomIn} disabled={zoomLevel >= 3}>
                <ZoomInOutlined />
              </Button>
              <Button onClick={handleRotateLeft} style={{ marginLeft: 8 }}>
                <RotateLeftOutlined />
              </Button>
              <Button onClick={handleRotateRight} style={{ marginLeft: 8 }}>
                <RotateRightOutlined />
              </Button>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
};
 
export default SelectedFilePreviewModal;